import { useState, useRef, useEffect } from 'react'
import { Icon, categoryIcons } from './Icons'
import '../styles/CategorySelect.css'

function CategorySelect({ categories, value, onChange, onCategoryCreate }) {
  const [isOpen, setIsOpen] = useState(false)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [showIconPicker, setShowIconPicker] = useState(false)
  const [newCategory, setNewCategory] = useState({
    id: '',
    label: '',
    icon: 'folder',
    description: '',
  })
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState('')
  const dropdownRef = useRef(null)

  // Get selected category info
  const selectedCategory = categories.find(c => c.id === value)
  const selectedIcon = categoryIcons.find(i => i.id === newCategory.icon) || categoryIcons.find(i => i.id === 'folder')

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
        if (!creating) {
          setShowCreateForm(false)
          setShowIconPicker(false)
          setNewCategory({ id: '', label: '', icon: 'folder', description: '' })
          setError('')
        }
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [creating])

  const handleSelect = (categoryId) => {
    onChange(categoryId)
    setIsOpen(false)
  }

  const handleCreateClick = (e) => {
    e.stopPropagation()
    setShowCreateForm(true)
  }

  const handleCreateSubmit = async (e) => {
    e.preventDefault()
    e.stopPropagation()
    setError('')

    if (!newCategory.id || !newCategory.label || !newCategory.icon) {
      setError('ID, Label, and Icon are required')
      return
    }

    // Validate ID format
    if (!/^[a-z0-9-]+$/.test(newCategory.id)) {
      setError('ID must be lowercase letters, numbers, and hyphens only')
      return
    }

    // Check for duplicate ID
    if (categories.some(c => c.id === newCategory.id)) {
      setError('A category with this ID already exists')
      return
    }

    setCreating(true)
    try {
      await onCategoryCreate({
        ...newCategory,
        order: categories.length,
      })
      // Select the newly created category
      onChange(newCategory.id)
      // Reset form
      setNewCategory({ id: '', label: '', icon: 'folder', description: '' })
      setShowCreateForm(false)
      setShowIconPicker(false)
      setIsOpen(false)
    } catch (err) {
      setError(err.message || 'Failed to create category')
    } finally {
      setCreating(false)
    }
  }

  const handleCancelCreate = (e) => {
    e.stopPropagation()
    setShowCreateForm(false)
    setShowIconPicker(false)
    setNewCategory({ id: '', label: '', icon: 'folder', description: '' })
    setError('')
  }

  const handleIconSelect = (iconId) => {
    setNewCategory({ ...newCategory, icon: iconId })
    setShowIconPicker(false)
  }

  return (
    <div className="category-select" ref={dropdownRef}>
      <div
        className={`category-select-trigger ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        {selectedCategory ? (
          <span className="selected-category">
            <span className="category-icon">
              <Icon name={selectedCategory.icon} size={20} />
            </span>
            <span className="category-label">{selectedCategory.label}</span>
          </span>
        ) : (
          <span className="placeholder">Select a category...</span>
        )}
        <span className="dropdown-arrow">&#9662;</span>
      </div>

      {isOpen && (
        <div className="category-select-dropdown">
          <div className="category-options">
            {categories.map((category) => (
              <div
                key={category.id}
                className={`category-option ${value === category.id ? 'selected' : ''}`}
                onClick={() => handleSelect(category.id)}
              >
                <span className="category-icon">
                  <Icon name={category.icon} size={20} />
                </span>
                <span className="category-label">{category.label}</span>
              </div>
            ))}
          </div>

          <div className="category-select-divider" />

          {!showCreateForm ? (
            <div className="create-category-trigger" onClick={handleCreateClick}>
              <span className="create-icon">+</span>
              <span>Create new category</span>
            </div>
          ) : (
            <div className="create-category-form" onClick={(e) => e.stopPropagation()}>
              <div className="create-form-header">New Category</div>

              {error && <div className="create-form-error">{error}</div>}

              <div className="create-form-row">
                <div className="create-form-icon-picker">
                  <button
                    type="button"
                    className="icon-picker-btn"
                    onClick={(e) => {
                      e.stopPropagation()
                      setShowIconPicker(!showIconPicker)
                    }}
                    title="Select icon"
                  >
                    <Icon name={newCategory.icon} size={24} />
                  </button>
                  {showIconPicker && (
                    <div className="mini-icon-picker" onClick={(e) => e.stopPropagation()}>
                      <div className="mini-icon-grid">
                        {categoryIcons.map((icon) => (
                          <button
                            key={icon.id}
                            type="button"
                            className={`mini-icon-option ${newCategory.icon === icon.id ? 'selected' : ''}`}
                            onClick={() => handleIconSelect(icon.id)}
                            title={icon.label}
                          >
                            <Icon name={icon.id} size={20} />
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <input
                  type="text"
                  placeholder="Label (e.g., Woodworking)"
                  value={newCategory.label}
                  onChange={(e) => setNewCategory({ ...newCategory, label: e.target.value })}
                  className="create-form-label-input"
                />
              </div>

              <input
                type="text"
                placeholder="ID (lowercase, e.g., woodworking)"
                value={newCategory.id}
                onChange={(e) => setNewCategory({ ...newCategory, id: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                className="create-form-id-input"
              />

              <div className="create-form-actions">
                <button
                  type="button"
                  className="create-form-cancel"
                  onClick={handleCancelCreate}
                  disabled={creating}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="create-form-submit"
                  onClick={handleCreateSubmit}
                  disabled={creating}
                >
                  {creating ? 'Creating...' : 'Create'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default CategorySelect
